import { Injectable, OnDestroy } from "@angular/core";

export interface TabMessage {
  type: "ping" | "pong" | "focus-me";
  tabId: string;
  timestamp: number;
}
function generateTabId(): string {
  return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

@Injectable({
  providedIn: "root",
})
export class SingleTabService implements OnDestroy {
  private channel: BroadcastChannel | null = null;
  private readonly channelName = "single-tab-coordinator";
  private readonly tabId = generateTabId();
  private readonly responseTimeout = 1000; // 1 second
  private isInitialized = false;

  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    // Check if browser supports BroadcastChannel
    if (!window.BroadcastChannel) {
      console.warn("BroadcastChannel not supported, allowing multiple tabs");
      this.isInitialized = true;
      return true;
    }

    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.setupMessageListener();

      const shouldProceed = await this.checkForExistingTabs();

      if (shouldProceed) {
        this.isInitialized = true;
        return true;
      } else {
        // This tab should close/redirect
        return false;
      }
    } catch (error) {
      console.error("Error initializing single tab service:", error);
      // Allow app to continue if there's an error
      this.isInitialized = true;
      return true;
    }
  }

  private setupMessageListener(): void {
    if (!this.channel) return;

    this.channel.addEventListener(
      "message",
      (event: MessageEvent<TabMessage>) => {
        const message = event.data;

        switch (message.type) {
          case "ping":
            // Another tab is asking if we exist
            this.respondToPing(message.tabId);
            break;

          case "pong":
            // We received a response, meaning another tab exists
            // This shouldn't happen in our flow, but good to handle
            break;

          case "focus-me":
            // Another tab is asking us to focus
            this.focusThisTab();
            break;
        }
      },
    );
  }

  private checkForExistingTabs(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.channel) {
        resolve(true);
        return;
      }

      let hasResponse = false;

      // Listen for responses
      const responseHandler = (event: MessageEvent<TabMessage>) => {
        const message = event.data;

        if (message.type === "pong" && message.tabId !== this.tabId) {
          hasResponse = true;
          this.handleExistingTabFound();
          resolve(false); // Don't proceed with this tab
        }
      };

      this.channel.addEventListener("message", responseHandler);

      // Send ping to check for existing tabs
      this.channel.postMessage({
        type: "ping",
        tabId: this.tabId,
        timestamp: Date.now(),
      } as TabMessage);

      // Wait for responses
      setTimeout(() => {
        this.channel?.removeEventListener("message", responseHandler);

        if (!hasResponse) {
          // No existing tabs found, this tab can proceed
          resolve(true);
        }
      }, this.responseTimeout);
    });
  }

  private respondToPing(requestingTabId: string): void {
    if (!this.channel || requestingTabId === this.tabId) return;

    // Send response indicating we exist
    this.channel.postMessage({
      type: "pong",
      tabId: this.tabId,
      timestamp: Date.now(),
    } as TabMessage);
  }

  private handleExistingTabFound(): void {
    // Ask the existing tab to focus itself
    if (this.channel) {
      this.channel.postMessage({
        type: "focus-me",
        tabId: this.tabId,
        timestamp: Date.now(),
      } as TabMessage);
    }

    // Try to close this tab or redirect
    this.closeOrRedirectTab();
  }

  private focusThisTab(): void {
    try {
      // Try to focus the window
      window.focus();

      // Also try to bring tab to front (limited browser support)
      if (document.hidden) {
        // Tab is in background, try to get attention
        this.flashTitle();
      }
    } catch (error) {
      console.warn("Could not focus tab:", error);
    }
  }

  private closeOrRedirectTab(): void {
    // Small delay to ensure message is sent
    setTimeout(() => {
      try {
        // Try to close the tab (works if opened by JavaScript)
        window.close();

        // If close didn't work, check if window is still open
        setTimeout(() => {
          if (!window.closed) {
            // Redirect to a "please close this tab" page
            this.redirectToClosePage();
          }
        }, 500);
      } catch (error) {
        console.warn("Could not close tab:", error);
        this.redirectToClosePage();
      }
    }, 100);
  }

  private redirectToClosePage(): void {
    // You can create a simple component that shows "Please close this tab"
    // or redirect to a specific route
    document.body.innerHTML = `
      <div style="
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
        flex-direction: column;
        font-family: Arial, sans-serif;
        background: #f5f5f5;
      ">
        <h2>REXTMS Application Already Open</h2>
        <p>This application is already running in another tab.</p>
        <p>Please close this tab and use the existing one.</p>
      </div>
    `;
  }

  private flashTitle(): void {
    const originalTitle = document.title;
    let flashCount = 0;
    const maxFlashes = 6;

    const flashInterval = setInterval(() => {
      document.title =
        flashCount % 2 === 0 ? "🔔 " + originalTitle : originalTitle;
      flashCount++;

      if (flashCount >= maxFlashes) {
        clearInterval(flashInterval);
        document.title = originalTitle;
      }
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.channel) {
      this.channel.close();
    }
  }

  // Public method to check if service is ready
  isReady(): boolean {
    return this.isInitialized;
  }
}
